/*
 * FEDERAÇÃO DOS PROFISSIONAIS DA BELEZA
 * FRONTEND - useAuth HOOK
 * UPDATED: 2026-03-25 — v2.1
 * FIXES: [1] response.user normalizado antes do dispatch (name + role obrigatórios)
 *        [2] checkAuth real com getMe() [3] Redux conectado
 *        [4] helpers isAffiliate/isStudent/isAdmin
 */

import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setUser, setLoading, setError,
  logout as logoutAction, restoreSession,
  selectUser, selectIsAuthenticated, selectAuthLoading,
  selectAffiliateId, selectStudentId, selectUserType,
} from '../store/slices/authSlice';
import { authService, LoginRequest } from '../services/authService';
import { TOKEN_KEY } from '../services/api';

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useAuth = () => {
  const dispatch = useDispatch();

  const user            = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const loading         = useSelector(selectAuthLoading);
  const affiliateId     = useSelector(selectAffiliateId);
  const studentId       = useSelector(selectStudentId);
  const userType        = useSelector(selectUserType);

  // ── Restaura sessão ao montar ─────────────────────────────────────────────
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) { dispatch(setLoading(false)); return; }

      dispatch(restoreSession()); // mostra cached enquanto verifica

      try {
        dispatch(setLoading(true));
        const freshUser = await authService.getMe(); // já retorna AuthUser normalizado
        dispatch(setUser(freshUser));
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;

        // Falhas temporárias (rede, timeout ou cold start do Render) não devem
        // apagar uma sessão válida que já foi restaurada do navegador.
        if (status === 401 || status === 403) dispatch(logoutAction());
        else dispatch(setLoading(false));
      }
    };
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (credentials: LoginRequest): Promise<void> => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      await authService.login(credentials);

      // saveSession já normalizou e preservou affiliateId/studentId e dados
      // de promoção. Reutilizar o cache evita perder esses campos no Redux.
      const normalized = authService.getCachedUser();
      if (!normalized) throw new Error('Não foi possível restaurar a sessão');

      dispatch(setUser(normalized));

    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message = err?.response?.data?.message ?? 'Credenciais inválidas';
      dispatch(setError(message));
      throw error;
    }
  }, [dispatch]);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async (): Promise<void> => {
    try { await authService.logout(); }
    finally { dispatch(logoutAction()); }
  }, [dispatch]);

  return {
    user,
    loading,
    isLoading:      loading,
    isAuthenticated,
    affiliateId,
    studentId,
    userType,
    isAdmin:        userType === 'admin',
    isAffiliate:    userType === 'affiliate',
    isStudent:      userType === 'student',
    login,
    logout,
  };
};

export default useAuth;
